"use client";

import NavUser from "@/components/NavUser";
import config from "@/config/config.json";
import Link from "next/link";
import Image from 'next/image';
import { usePathname, useRouter } from "next/navigation";
import React, { Suspense, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import logo from "public/images/logo.svg"
import { BsStars } from "react-icons/bs";
import { HiOutlineBookOpen, HiOutlineUserGroup } from "react-icons/hi";
import { IoChatbubbleEllipsesOutline } from "react-icons/io5";
import Cookies from "js-cookie";
import EditEmailModal from "@/components/comix/EditEmailModal";
import ConfirmNavigationModal from "@/components/shared/ConfirmNavigationModal";
import { useNavigationGuard } from "@/lib/navigation-guard";
import { trackEvent } from "@/lib/amplitude";
import { siteConfig } from "@/lib/seo-config";
import { pushThemeColor } from "@/lib/theme-color";

const Header: React.FC <{children: any}> = ( {children} ) => {
  const [navbarShadow, setNavbarShadow] = useState(false);
  const { settings } = config;
  const pathname = usePathname();
  const router = useRouter();
  const [showSidebar, setShowSidebar] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [editEmailOpen, setEditEmailOpen] = useState(false);

  // Set by pages doing something the user shouldn't casually navigate away from mid-flight
  // (currently: comic generation on /comix/preview) via `setNavigationGuard` — see
  // src/lib/navigation-guard.ts. When active, the logo and every real in-app link below
  // intercept their click and ask for confirmation instead of navigating immediately. The
  // `mailto:` contact link is deliberately excluded — opening a mail client doesn't lose the
  // page.
  const navGuard = useNavigationGuard();
  const [pendingHref, setPendingHref] = useState<string | null>(null);

  const handleNavClick = (href: string, label?: string) => (e: React.MouseEvent) => {
    if (label) trackEvent("Nav - Link Clicked", { label, href });
    if (navGuard.active) {
      e.preventDefault();
      setPendingHref(href);
      return;
    }
    setShowSidebar(false);
  };

  const confirmLeave = () => {
    const href = pendingHref;
    setPendingHref(null);
    setShowSidebar(false);
    if (href) router.push(href);
  };

  useEffect(() => {
    window.scroll(0, 0);
    setShowSidebar(false);
  }, [pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setNavbarShadow(window.scrollY > 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const storedSessionId = Cookies.get('comixSessionId');
    if (!storedSessionId) return;
    setSessionId(storedSessionId);
    fetch(`/api/comix/session?sessionId=${storedSessionId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.userEmail) setUserEmail(data.userEmail); })
      .catch(() => {});
  }, [showSidebar]);

  const handleToggleSidebar = () => {
    trackEvent("Nav - Menu Toggled", { open: !showSidebar });
    setShowSidebar(prev => !prev);
  };

  // The sidebar's dark overlay was tinting iOS Safari's chrome via its own auto-detection,
  // with nothing to revert it on close (unlike every other full-screen modal, which explicitly
  // pushes/pops via pushThemeColor). Managing it explicitly here fixes that stuck-color bug.
  useEffect(() => {
    if (!showSidebar) return;
    return pushThemeColor('#000000');
  }, [showSidebar]);

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Internal dev tool — runs its own compact top bar and wants the vertical space back.
  if (pathname?.startsWith('/prompt-playground')) return null;

  // The flipbook reader needs every pixel of vertical space it can get on a small screen —
  // desktop keeps the header (there's room), mobile hides it entirely on this page only.
  // Deferred to after mount (rather than read directly from usePathname) because this page
  // can be reached through a rewrite, where the server-rendered pathname and the client's
  // actual browser pathname diverge and cause a hydration mismatch on this className.
  const hideOnMobile = mounted && pathname === '/comix/preview';

  return (
    <>
    <header className={`header bg-white z-30 ${hideOnMobile ? "hidden md:block" : ""} ${settings.sticky_header ? "sticky top-0" : ""} border-b border-gray-200 ${navbarShadow ? "shadow-sm" : "shadow-none"}`}>
      {/* px-4 / md:px-0 pin the header's own left/right padding (1rem on mobile, flush on
          desktop) instead of inheriting the shared .container class's padding — there are
          two conflicting .container rules in the compiled CSS (Tailwind's own container
          plugin at 2rem, and a leftover Bootstrap-style .container rule at 1rem), so this
          keeps the header predictable without editing the shared class and risking every
          other .container usage on the site. */}
      <nav className="container mx-auto max-w-2xl md:max-w-[1040px] px-4 md:px-0 grid grid-cols-3 items-center">
        {/* Hamburger — left */}
        <div className="flex justify-start">
          <button
            id="nav-toggle"
            className="focus:outline-none ml-3"
            onClick={handleToggleSidebar}
            aria-label={showSidebar ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={showSidebar}
          >
            <svg className="h-5 w-5 fill-current text-black" viewBox="0 0 20 20" aria-hidden="true">
              <path d="M0 3h20v2H0V3z m0 6h20v2H0V9z m0 6h20v2H0V0z" />
            </svg>
          </button>
        </div>

        {/* Logo — center */}
        <div className="flex justify-center">
          <Link href="/" onClick={handleNavClick('/', 'Logo')} className="flex items-center justify-center cursor-pointer">
            <Image src={logo} alt="logo" width={141} height={28} style={{ height: '27px', width: 'auto', maxWidth: 'none' }} />
          </Link>
        </div>

        {/* Cart + user — right */}
        <div className="flex items-center justify-end space-x-3">
          <Suspense fallback={children[0]}>{children[1]}</Suspense>
          <NavUser />
        </div>
      </nav>
    </header>

    {/* Overlay + sidebar are portaled to <body> so they always paint above
        page content, regardless of stacking contexts created by sticky/fixed
        elements elsewhere on the page (header itself included). */}
    {mounted && createPortal(
      <>
      {/* Overlay */}
      {showSidebar && (
        <div className="fixed inset-0 bg-black/50 z-[60]" onClick={handleToggleSidebar} />
      )}

      {/* Sidebar — slides from right, Airbnb style */}
      <aside
        className={`fixed top-0 left-0 h-full w-[320px] bg-white z-[70] flex flex-col overflow-y-auto overscroll-contain shadow-2xl transition-transform duration-300 ${
          showSidebar ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo + close row */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4">
          <Image src={logo} alt={`${siteConfig.name} logo`} width={141} height={28} style={{ height: '28px', width: 'auto' }} />
          <button
            id="btn-sidebar-close"
            onClick={handleToggleSidebar}
            aria-label="Close navigation menu"
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M1 1l10 10M11 1L1 11" />
            </svg>
          </button>
        </div>

        {/* Email card */}
        <div className="px-4 pb-6">
          {userEmail ? (
            <div className="flex items-center justify-between gap-2 border border-gray-200 rounded-2xl px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/images/comix/select/mail.svg" alt="" width={18} height={18} className="flex-shrink-0 opacity-50" />
                <span className="text-[15px] font-medium text-[#222] truncate">{userEmail}</span>
              </div>
              <button type="button" onClick={() => setEditEmailOpen(true)} className="text-[14px] text-[#222] underline underline-offset-2 flex-shrink-0">
                Edit
              </button>
            </div>
          ) : (
            <div
              className="rounded-2xl px-4 py-4"
              style={{
                background: 'linear-gradient(#FFF0F6, #FFF0F6) padding-box, linear-gradient(to right, #F52F81, #FD7468) border-box',
                border: '1.5px solid transparent',
              }}
            >
              <p className="text-[17px] font-semibold text-[#222] mb-1">
                Save your progress
              </p>
              <p className="text-[14px] text-gray-500 leading-snug mb-3">Add your email so your characters and stories are always safe.</p>
              <button
                type="button"
                onClick={() => setEditEmailOpen(true)}
                className="w-full text-[15px] font-semibold text-white py-2.5 transition-all hover:brightness-90"
                style={{
                  background: 'linear-gradient(to right, #F52F81 0%, #FD7468 100%) padding-box, linear-gradient(to bottom, #FB5074 0%, #FD9CAE 100%) border-box',
                  border: '1px solid transparent',
                  borderRadius: '16px',
                  boxShadow: '0 3px 2px 0 rgba(250, 77, 117, 0.15)',
                }}
              >
                Add your email
              </button>
            </div>
          )}
        </div>

        <div className="h-px bg-gray-100 mx-4" />

        {/* Nav items */}
        <nav className="flex-1 px-3 py-2">
          {/* A returning user with an email + session already has characters — send them
              straight to /comix/stories to write a new one, instead of the marketing
              landing page they'd normally hit before ever creating a character. */}
          <Link href={userEmail && sessionId ? `/comix/stories?sessionId=${sessionId}` : '/comix'} onClick={handleNavClick(userEmail && sessionId ? `/comix/stories?sessionId=${sessionId}` : '/comix', 'Create a new comic')} className="flex items-center gap-4 px-3 py-[15px] rounded-xl hover:bg-gray-50 transition-colors">
            <BsStars className="text-[#222] flex-shrink-0 w-[17px] h-[17px]" />
            <span className="text-[17px] font-semibold text-[#222]">Create a new comic</span>
          </Link>
          <Link href={sessionId ? `/comix/select?sessionId=${sessionId}` : '/comix/select'} onClick={handleNavClick(sessionId ? `/comix/select?sessionId=${sessionId}` : '/comix/select', 'My characters')} className="flex items-center gap-4 px-3 py-[15px] rounded-xl hover:bg-gray-50 transition-colors">
            <HiOutlineUserGroup className="text-[#222] flex-shrink-0 w-[17px] h-[17px]" />
            <span className="text-[17px] font-semibold text-[#222]">My characters</span>
          </Link>
          {userEmail && sessionId && (
            <Link href={`/comix/stories?sessionId=${sessionId}`} onClick={handleNavClick(`/comix/stories?sessionId=${sessionId}`, 'My comics')} className="flex items-center gap-4 px-3 py-[15px] rounded-xl hover:bg-gray-50 transition-colors">
              <HiOutlineBookOpen className="text-[#222] flex-shrink-0 w-[17px] h-[17px]" />
              <span className="text-[17px] font-semibold text-[#222]">My comics</span>
            </Link>
          )}
          <div className="h-px bg-gray-100 my-2 mx-3" />
          <Link href={`mailto:${siteConfig.emails.general}`} onClick={() => { trackEvent("Nav - Link Clicked", { label: 'Contact us', href: `mailto:${siteConfig.emails.general}` }); setShowSidebar(false); }} className="flex items-center gap-4 px-3 py-[15px] rounded-xl hover:bg-gray-50 transition-colors">
            <IoChatbubbleEllipsesOutline className="text-[#222] flex-shrink-0 w-[17px] h-[17px]" />
            <span className="text-[17px] font-semibold text-[#222]">Contact us</span>
          </Link>
        </nav>

        {/* Footer legal links */}
        <div className="h-px bg-gray-100 mx-4" />
        <div className="px-6 py-5 flex flex-col gap-1">
          <Link href="/terms-services" onClick={handleNavClick('/terms-services', 'Terms of Use')} className="py-1 text-[15px] text-gray-500 hover:text-gray-800 hover:underline underline-offset-2">Terms of Use</Link>
          <Link href="/privacy-policy" onClick={handleNavClick('/privacy-policy', 'Privacy Policy')} className="py-1 text-[15px] text-gray-500 hover:text-gray-800 hover:underline underline-offset-2">Privacy Policy</Link>
          <Link href="/return-policy" onClick={handleNavClick('/return-policy', 'Return Policy')} className="py-1 text-[15px] text-gray-500 hover:text-gray-800 hover:underline underline-offset-2">Return Policy</Link>
        </div>

      </aside>

      <EditEmailModal
        open={editEmailOpen}
        sessionId={sessionId}
        initialEmail={userEmail ?? ''}
        onClose={() => setEditEmailOpen(false)}
        onSaved={(email) => { setUserEmail(email); setEditEmailOpen(false); }}
      />

      <ConfirmNavigationModal
        isOpen={pendingHref !== null}
        message={navGuard.message}
        onStay={() => setPendingHref(null)}
        onLeave={confirmLeave}
      />
      </>,
      document.body
    )}
    </>
  );
};

export default Header;
